import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { PageHeader } from "@/components/app/page-header";
import { HelpPanel } from "@/components/app/help-panel";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { listBounceLog } from "@/lib/bounce-log.functions";

export const Route = createFileRoute("/_authenticated/bounce-log")({
  component: BounceLogPage,
  head: () => ({ meta: [{ title: "Bounce-Log — InboxPulse" }] }),
});

type BounceRow = {
  id: string;
  received_at: string;
  tag: string;
  recipient_email: string;
  sender_email: string | null;
  campaign_name: string | null;
  bounce_type: string;
  bounce_reason: string | null;
  provider: string | null;
  message_id: string | null;
  diagnostic_code: string | null;
  remote_mta: string | null;
  reporting_mta: string | null;
  status_code: string | null;
  action: string | null;
  arrival_date: string | null;
  subject: string | null;
  raw_payload: unknown;
  telegram_chat_id: string | null;
  telegram_message_text: string | null;
  telegram_sent: boolean;
  telegram_error: string | null;
  telegram_message_id: number | null;
};

function BounceLogPage() {
  const fetchBounces = useServerFn(listBounceLog);
  const [search, setSearch] = useState("");
  const [bounceType, setBounceType] = useState<"all" | "hard" | "soft" | "block">("all");
  const [telegram, setTelegram] = useState<"all" | "sent" | "failed">("all");
  const [selected, setSelected] = useState<BounceRow | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["bounce-log", search, bounceType, telegram],
    queryFn: () =>
      fetchBounces({
        data: {
          limit: 100,
          search: search || undefined,
          bounce_type: bounceType === "all" ? undefined : bounceType,
          telegram,
        },
      }),
  });

  const rows = (data?.bounces ?? []) as BounceRow[];

  return (
    <div className="p-8 space-y-6">
      <PageHeader
        title="Bounce-Log"
        description="Alle verifizierten Bounces inkl. der gesendeten Telegram-Nachricht – Klick auf eine Zeile zeigt Roh-Payload und Telegram-Text."
      />

      <HelpPanel
        title="Hilfe: Was wurde gemeldet?"
        what={
          <>
            Das ist das <b>Logbuch</b>: Welcher Bounce wurde wann erkannt? Wurde eine
            Telegram-Nachricht verschickt? Was stand drin?
          </>
        }
        why={
          <>
            Falls du eine Telegram-Warnung nicht bekommen hast, schaust du hier — entweder steht es
            im Log oder es wurde nichts gesendet (Filter zu streng).
          </>
        }
        how={[
          <>Liste scrollen — neueste oben.</>,
          <>
            Auf eine Zeile klicken: Original-Bounce vom Mail-Server + gesendeter Telegram-Text
            werden angezeigt.
          </>,
          <>
            Kein Telegram-Eintrag? In <b>Alarm-Regeln</b> prüfen, ob der Auslöser passt.
          </>,
        ]}
        tips={[
          <>
            Roh-Payload ist die Original-Antwort des Mail-Servers — nützlich für tiefere
            Fehlersuche.
          </>,
        ]}
        glossary={[
          {
            term: "Payload",
            def: "Der eigentliche Daten-Inhalt einer Nachricht — hier die Bounce-Meldung des Servers.",
          },
          {
            term: "Verifiziert",
            def: "Wir haben geprüft: ja, das ist wirklich ein Bounce zu einer von uns versandten Mail.",
          },
        ]}
      />

      <Card className="p-4 bg-card/60 border-border">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label>Suche</Label>
            <Input
              placeholder="Empfänger, Tag, Message-ID…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div>
            <Label>Bounce-Typ</Label>
            <Select value={bounceType} onValueChange={(v) => setBounceType(v as typeof bounceType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle</SelectItem>
                <SelectItem value="hard">Hard</SelectItem>
                <SelectItem value="soft">Soft</SelectItem>
                <SelectItem value="block">Block</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Telegram-Status</Label>
            <Select value={telegram} onValueChange={(v) => setTelegram(v as typeof telegram)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle</SelectItem>
                <SelectItem value="sent">Gesendet</SelectItem>
                <SelectItem value="failed">Fehlgeschlagen</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <Card className="p-0 bg-card/60 border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-muted-foreground">
              <tr>
                <th className="text-left p-3">Zeit</th>
                <th className="text-left p-3">Typ</th>
                <th className="text-left p-3">Empfänger</th>
                <th className="text-left p-3">Absender</th>
                <th className="text-left p-3">Kampagne</th>
                <th className="text-left p-3">Grund</th>
                <th className="text-left p-3">Telegram</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={7} className="p-6 text-center text-muted-foreground">
                    Lade…
                  </td>
                </tr>
              )}
              {!isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-6 text-center text-muted-foreground">
                    Noch keine Bounces protokolliert.
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr
                  key={r.id}
                  className="border-t border-border hover:bg-muted/30 cursor-pointer"
                  onClick={() => setSelected(r)}
                >
                  <td className="p-3 whitespace-nowrap">
                    {new Date(r.received_at).toLocaleString()}
                  </td>
                  <td className="p-3">
                    <Badge variant={r.bounce_type === "hard" ? "destructive" : "secondary"}>
                      {r.bounce_type}
                    </Badge>
                  </td>
                  <td className="p-3">{r.recipient_email}</td>
                  <td className="p-3">{r.sender_email ?? "—"}</td>
                  <td className="p-3">{r.campaign_name ?? "—"}</td>
                  <td className="p-3 max-w-[280px] truncate" title={r.bounce_reason ?? ""}>
                    {r.bounce_reason ?? "—"}
                  </td>
                  <td className="p-3">
                    {r.telegram_sent ? (
                      <Badge className="bg-emerald-600 hover:bg-emerald-600">Gesendet</Badge>
                    ) : (
                      <Badge variant="destructive" title={r.telegram_error ?? ""}>
                        Fehler
                      </Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Bounce-Details {selected?.recipient_email ? `– ${selected.recipient_email}` : ""}
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Empfangen" value={new Date(selected.received_at).toLocaleString()} />
                <Field label="Typ" value={selected.bounce_type} />
                <Field label="Empfänger" value={selected.recipient_email} />
                <Field label="Absender" value={selected.sender_email} />
                <Field label="Kampagne" value={selected.campaign_name} />
                <Field label="Provider" value={selected.provider} />
                <Field label="Tag" value={selected.tag} mono />
                <Field label="Message-ID" value={selected.message_id} mono />
                <Field label="Status-Code" value={selected.status_code} />
                <Field label="Action" value={selected.action} />
                <Field label="Remote MTA" value={selected.remote_mta} />
                <Field label="Reporting MTA" value={selected.reporting_mta} />
                <Field label="Arrival" value={selected.arrival_date} />
                <Field label="Betreff" value={selected.subject} />
              </div>
              <div>
                <Label className="text-muted-foreground">Diagnostic Code</Label>
                <pre className="mt-1 p-3 bg-muted/40 rounded text-xs whitespace-pre-wrap break-words">
                  {selected.diagnostic_code ?? "—"}
                </pre>
              </div>
              <div>
                <Label className="text-muted-foreground">Bounce-Grund</Label>
                <pre className="mt-1 p-3 bg-muted/40 rounded text-xs whitespace-pre-wrap break-words">
                  {selected.bounce_reason ?? "—"}
                </pre>
              </div>
              <div>
                <Label className="text-muted-foreground">
                  Telegram-Nachricht{" "}
                  {selected.telegram_sent ? (
                    <Badge className="bg-emerald-600 hover:bg-emerald-600 ml-2">
                      Gesendet (msg_id {selected.telegram_message_id ?? "—"})
                    </Badge>
                  ) : (
                    <Badge variant="destructive" className="ml-2">
                      Nicht gesendet: {selected.telegram_error ?? "unbekannt"}
                    </Badge>
                  )}
                </Label>
                <div className="text-xs text-muted-foreground mt-1">
                  Chat: <code>{selected.telegram_chat_id ?? "—"}</code>
                </div>
                <pre className="mt-2 p-3 bg-muted/40 rounded text-xs whitespace-pre-wrap break-words">
                  {selected.telegram_message_text ?? "—"}
                </pre>
              </div>
              <div>
                <Label className="text-muted-foreground">Raw Payload</Label>
                <pre className="mt-1 p-3 bg-muted/40 rounded text-xs whitespace-pre-wrap break-words max-h-72 overflow-auto">
                  {JSON.stringify(selected.raw_payload, null, 2)}
                </pre>
              </div>
              <div className="flex justify-end">
                <Button variant="outline" onClick={() => setSelected(null)}>
                  Schließen
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: string | null | undefined;
  mono?: boolean;
}) {
  return (
    <div>
      <Label className="text-muted-foreground">{label}</Label>
      <div className={mono ? "font-mono text-xs break-all" : "text-sm"}>{value ?? "—"}</div>
    </div>
  );
}
